import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    Icon,
    IconContainer,
    TooltipContainer,
    TooltipContent,
    TooltipTriangle,
    PortalTooltipContainer,
    PortalTooltipTrigger,
    PortalTooltipLayer,
    PortalTooltipTriangle,
    PortalTooltipContent,
} from 'src/domains/players/webview/components/Account/summaryTab/accountSummary/Tooltip/Tooltip.style';

export type TooltipType = 'default' | 'info' | 'error';

type TooltipPropsType = {
    content: React.ReactNode;
    type: TooltipType;
    /** Optional test id; defaults to `{type}-tooltip`. */
    dataTest?: string;
    /**
     * Portal + fixed positioning — survives overflow clipping and overlays.
     * Default false keeps the legacy CSS-hover tooltip used in account summary.
     */
    portal?: boolean;
    /** Horizontal align of the portal tooltip relative to the trigger. */
    align?: 'start' | 'end';
};

const TOOLTIP_MAX_WIDTH_PX = 260;
const TOOLTIP_VIEWPORT_PADDING_PX = 8;
const TOOLTIP_OFFSET_FROM_TRIGGER_PX = 10;

interface TooltipCoordsType {
    top: number;
    left: number;
    width: number;
    triangleOffset: number;
}

const getClippingBounds = (element: HTMLElement): { left: number; right: number } => {
    let boundsLeft = 0;
    let boundsRight = window.innerWidth;
    let current: HTMLElement | null = element.parentElement;

    while (current !== null) {
        const style = window.getComputedStyle(current);
        if (style.overflowX === 'hidden' || style.overflowX === 'auto' || style.overflowX === 'scroll') {
            const rect = current.getBoundingClientRect();
            boundsLeft = Math.max(boundsLeft, rect.left);
            boundsRight = Math.min(boundsRight, rect.right);
        }
        current = current.parentElement;
    }

    return { left: boundsLeft, right: boundsRight };
};

const measureTooltipPosition = (trigger: HTMLElement, align: 'start' | 'end'): TooltipCoordsType => {
    const triggerRect = trigger.getBoundingClientRect();
    const bounds = getClippingBounds(trigger);
    const availableWidth = Math.max(0, bounds.right - bounds.left - TOOLTIP_VIEWPORT_PADDING_PX * 2);
    const width = Math.min(TOOLTIP_MAX_WIDTH_PX, availableWidth);
    const preferredLeft = align === 'end' ? triggerRect.right - width : triggerRect.left;
    const minLeft = bounds.left + TOOLTIP_VIEWPORT_PADDING_PX;
    const maxLeft = bounds.right - width - TOOLTIP_VIEWPORT_PADDING_PX;
    const left = Math.max(minLeft, Math.min(preferredLeft, maxLeft));
    const iconCenterX = triggerRect.left + triggerRect.width / 2;
    const triangleOffset = Math.max(12, Math.min(iconCenterX - left, width - 12));

    return {
        top: triggerRect.bottom + TOOLTIP_OFFSET_FROM_TRIGGER_PX,
        left,
        width,
        triangleOffset,
    };
};

const AnchoredTooltip = ({ content, type, dataTest }: TooltipPropsType): JSX.Element => {
    return (
        <TooltipContainer data-test={dataTest ?? `${type}-tooltip`}>
            <IconContainer>
                <Icon type={type} />
            </IconContainer>
            <TooltipContent
                className='tooltip-content'
                type={type}
            >
                {content}
            </TooltipContent>
            <TooltipTriangle
                className='tooltip-triangle'
                type={type}
            />
        </TooltipContainer>
    );
};

const PortalTooltip = ({ content, type, dataTest, align = 'start' }: TooltipPropsType): JSX.Element => {
    const triggerRef = useRef<HTMLButtonElement>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [coords, setCoords] = useState<TooltipCoordsType | null>(null);
    const testId = dataTest ?? `${type}-tooltip`;

    const updatePosition = useCallback((): void => {
        const trigger = triggerRef.current;
        if (trigger === null) {
            return;
        }
        setCoords(measureTooltipPosition(trigger, align));
    }, [align]);

    const show = (): void => {
        updatePosition();
        setIsOpen(true);
    };

    const hide = (): void => {
        setIsOpen(false);
    };

    useLayoutEffect(() => {
        if (isOpen === false) {
            return;
        }

        updatePosition();
        window.addEventListener('scroll', updatePosition, true);
        window.addEventListener('resize', updatePosition);

        return (): void => {
            window.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
        };
    }, [isOpen, updatePosition]);

    const tooltipLayer =
        isOpen === true && coords !== null && typeof document !== 'undefined'
            ? createPortal(
                  <PortalTooltipLayer
                      data-test={`${testId}-content`}
                      role='tooltip'
                      style={{ top: coords.top, left: coords.left, width: coords.width }}
                  >
                      <PortalTooltipTriangle
                          aria-hidden={true}
                          type={type}
                          style={{ left: coords.triangleOffset }}
                      />
                      <PortalTooltipContent type={type}>{content}</PortalTooltipContent>
                  </PortalTooltipLayer>,
                  document.body
              )
            : null;

    return (
        <PortalTooltipContainer
            data-test={testId}
            onMouseEnter={show}
            onMouseLeave={hide}
        >
            <PortalTooltipTrigger
                ref={triggerRef}
                type='button'
                aria-expanded={isOpen}
                data-test={`${testId}-trigger`}
                onFocus={show}
                onBlur={hide}
            >
                <Icon type={type} />
            </PortalTooltipTrigger>
            {tooltipLayer}
        </PortalTooltipContainer>
    );
};

export const Tooltip = ({ content, type, dataTest, portal = false, align }: TooltipPropsType): JSX.Element => {
    if (portal === true) {
        return (
            <PortalTooltip
                content={content}
                type={type}
                dataTest={dataTest}
                align={align}
            />
        );
    }

    return (
        <AnchoredTooltip
            content={content}
            type={type}
            dataTest={dataTest}
        />
    );
};
